import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireTenantUser } from '@/integrations/supabase/session';
import { useToast } from '@/hooks/use-toast';
import { useVisibility } from '@/hooks/useVisibility';
import { formatDateInputInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { BankReconciliation, ReconciliationEntry, ReconciliationPaymentMethod, PaymentMethodTotals } from '@/types/finance';

const PAYMENT_METHOD_LABELS: Record<ReconciliationPaymentMethod, string> = {
  cash: 'Cash',
  card: 'Card / EFTPOS',
  bank_transfer: 'Bank Transfer',
  other: 'Other',
};

interface UseBankReconciliationProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  systemTotals: PaymentMethodTotals | null;
}

export const useBankReconciliation = ({
  startDate,
  endDate,
  timezone = DEFAULT_TIMEZONE,
  systemTotals,
}: UseBankReconciliationProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canSeeCompanyFinancials } = useVisibility();

  const periodStart = formatDateInputInTz(startDate, timezone);
  const periodEnd = formatDateInputInTz(endDate, timezone);

  // Fetch existing reconciliation entries for the period
  const { data: savedReconciliations, isLoading } = useQuery({
    queryKey: ['reports', 'bank-reconciliations', periodStart, periodEnd],
    enabled: canSeeCompanyFinancials,
    queryFn: async (): Promise<BankReconciliation[]> => {
      const { data, error } = await supabase
        .from('bank_reconciliations')
        .select('*')
        .eq('period_start', periodStart)
        .eq('period_end', periodEnd);

      if (error) throw error;
      return (data || []) as BankReconciliation[];
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  // Build reconciliation entries combining system totals with saved data
  const entries: ReconciliationEntry[] = (() => {
    if (!systemTotals) return [];

    const methods: ReconciliationPaymentMethod[] = ['cash', 'card', 'bank_transfer', 'other'];
    
    return methods.map(method => {
      const saved = savedReconciliations?.find(r => r.payment_method === method);
      const systemAmount = systemTotals[method] || 0;
      const actualReceived = saved?.actual_received ?? null;
      
      return {
        payment_method: method,
        label: PAYMENT_METHOD_LABELS[method],
        system_amount: systemAmount,
        actual_received: actualReceived,
        variance: actualReceived !== null ? actualReceived - systemAmount : 0,
        notes: saved?.notes || '',
        status: saved?.status || 'draft',
      };
    });
  })();

  // Calculate totals
  const totals = {
    system: entries.reduce((sum, e) => sum + e.system_amount, 0),
    actual: entries.reduce((sum, e) => sum + (e.actual_received || 0), 0),
    variance: entries.reduce((sum, e) => sum + e.variance, 0),
  };

  // Check if any entries have been saved
  const hasExistingData = (savedReconciliations?.length || 0) > 0;

  // Determine overall status
  const overallStatus = (() => {
    if (!hasExistingData) return 'not_started';
    const allReconciled = entries.every(e => e.status === 'reconciled');
    if (allReconciled) return 'reconciled';
    const someEntered = entries.some(e => e.actual_received !== null);
    if (someEntered) return 'in_progress';
    return 'not_started';
  })();

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (entriesToSave: ReconciliationEntry[]) => {
      const { user, tenantId: companyId } = await requireTenantUser();

      // Upsert each entry
      for (const entry of entriesToSave) {
        const { error } = await supabase
          .from('bank_reconciliations')
          .upsert({
            company_id: companyId,
            period_start: periodStart,
            period_end: periodEnd,
            payment_method: entry.payment_method,
            system_amount: entry.system_amount,
            actual_received: entry.actual_received,
            notes: entry.notes || null,
            status: entry.status,
            reconciled_by: entry.status === 'reconciled' ? user.id : null,
            reconciled_at: entry.status === 'reconciled' ? new Date().toISOString() : null,
          }, {
            onConflict: 'company_id,period_start,period_end,payment_method',
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'bank-reconciliations'] });
      toast({
        title: 'Reconciliation saved',
        description: 'Your bank reconciliation has been saved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error saving reconciliation',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    entries,
    totals,
    overallStatus,
    hasExistingData,
    isLoading,
    isSaving: saveMutation.isPending,
    save: saveMutation.mutate,
  };
};
